import type { PlanCatalogEntry, SubscriptionStatus } from './types';

export type PlanCtaKind =
  | 'current' // this is the workspace's current plan
  | 'manage' // current paid plan — manage/cancel
  | 'keep' // current plan, on trial — add payment to keep it
  | 'choose' // a paid plan the workspace isn't on — subscribe to it
  | 'switch' // a paid plan to move a LIVE subscription onto — change-plan, not checkout
  | 'downgrade' // the free plan while on a paid one
  | 'contact'; // a non-self-serve (enterprise) plan

export interface PlanCta {
  kind: PlanCtaKind;
  label: string;
  /** Primary (filled) vs. secondary emphasis for the button. */
  primary: boolean;
  disabled: boolean;
}

/**
 * Derive a plan card's call-to-action by comparing the catalog plan to the
 * workspace's current subscription. Pure — no side effects — so it's unit
 * tested across every state. The server owns actual eligibility (duplicate
 * guard, trial); this only picks the affordance to show.
 */
/**
 * `||`, not `&&`: the server sets brand and last4 together (billing.rs), so
 * either one present already means a live Stripe subscription exists — that's
 * what routes a workspace to change-plan instead of first-time checkout. This
 * is deliberately looser than `formatSavedCard`, which requires both to
 * render `"Visa ••••4242"` and returns null otherwise.
 */
export function hasSavedCard(subscription: SubscriptionStatus): boolean {
  return subscription.paymentMethodBrand !== null || subscription.paymentMethodLast4 !== null;
}

export function planCta(plan: PlanCatalogEntry, subscription: SubscriptionStatus): PlanCta {
  const isCurrent = plan.code === subscription.planCode;
  // A trial with a card saved is a LIVE bound Stripe subscription — checkout
  // answers 409 already_subscribed for it. Only a card-less trial can still
  // be converted by a first checkout, and `status` alone cannot tell the two
  // apart; that is what the payment-method fields are for.
  const cardlessTrial = subscription.status === 'trialing' && !hasSavedCard(subscription);
  // Basic is the only free plan. Don't infer "free" from monthlyCredits === 0 —
  // team tiers are per-seat and carry a flat monthlyCredits of 0, which is NOT
  // free (their price lives in credits_per_seat / Stripe).
  const isFree = plan.code === 'basic';

  if (!plan.isSelfServe) {
    return { kind: 'contact', label: 'Contact sales', primary: false, disabled: false };
  }

  if (isCurrent) {
    if (cardlessTrial) {
      return { kind: 'keep', label: `Add payment to keep ${plan.name}`, primary: true, disabled: false };
    }
    // The current plan — free or paid — is a disabled "Current plan" marker in
    // the catalog. Managing/cancelling lives on the status card, not here.
    return { kind: 'current', label: 'Current plan', primary: false, disabled: true };
  }

  // Not the current plan.
  if (isFree) {
    return { kind: 'downgrade', label: 'Downgrade to Basic', primary: false, disabled: false };
  }

  // A paid plan the workspace isn't on. A workspace whose subscription is
  // already live at Stripe moves between tiers with change-plan; only one
  // without a live binding can start a first checkout.
  if (subscription.status === 'trialing' && !cardlessTrial) {
    return { kind: 'switch', label: `Switch to ${plan.name}`, primary: true, disabled: false };
  }

  // The 14-day trial is granted automatically at signup, not started from the
  // catalog, so there is no "start trial" CTA here.
  return { kind: 'choose', label: `Choose ${plan.name}`, primary: true, disabled: false };
}
